/**
 * 最初の公開で見せない機能（founder 決定 2026-09-27・docs/MASTER-PLAN.md §1c）。SDK の切り替えはこの 1 つの定数だけ。
 *
 * WHY 見せないか: Kumo はクリエイターに「フラットなバックエンド」として出す。ランキング・実績・通貨・ソーシャル・運営機能のような
 *   ゲーム向けの出来合いの機能は Kumo の機能として並べず、クリエイター自身の DB と Functions に組む Skill（レシピ）で配る
 *   （ランキングの Skill は各テンプレートの .claude/skills/leaderboard/。コードも責任もクリエイターのもの）。
 * WHY 内部のコードを消さないか: 後で出す判断に備える。gamedata.ts・social.ts・liveops.ts・lazy.ts はそのまま動く。
 *   ここの一覧から外せば、init() の返す窓口（kumo.leaderboards 等）と型（Kumo）が元どおりになる。
 *
 * SDK でしていること: init() の返すオブジェクトから、隠す機能の名前空間を外す（型からも。index.ts の Kumo）。
 *   本当に使えないようにするのはサーバー（Core が ON にする操作を断る）。SDK は「公開の関数として見せない」だけ。
 *   閉じている部品のルートは、どの鍵でもサーバーが 403 `feature_unavailable`（details.feature）を返す（Core 2026-09-27。feature_disabled とは別。
 *   本人は ON にできない）。SDK はそのまま KumoError（code 'feature_unavailable'）として渡す。stats の応答の unlocked は閉じている間は常に []
 * この一覧は index.ts から export しない（公開の API に隠す機能の名前を出さないため）。
 *
 * キーはサーバーの機能の登録簿（apps/server/src/modules/config/features.ts）のキーそのもの。実在は launchHidden.test.ts が確かめる。
 */

import type { PublicStatsResult } from './gamedata.js';

/** 最初の公開で見せない機能のキー（サーバーの features.ts の登録簿のキー）。空にすれば全部出る */
export const LAUNCH_HIDDEN_FEATURES = [
  // ランキング・実績（Skill で作る）
  'leaderboards',
  'achievements',
  // 交換所（kumo.inventory.exchanges / exchange = 下の SDK_METHODS）。所持品の「読む・使う」（kumo.inventory.get / consume）は
  // 2026-09-27 founder 決定（案 A）で出した。課金のコインとリワード広告の報酬の受け取り先だから。
  // サーバーも GET /v1/inventory と POST /v1/inventory/consume だけを開け、付与・他人の分・交換所は閉じたまま（Core 2026-09-27）。
  // 2026-09-29: 課金と広告を閉じたので、所持品ごと閉じた（下の 'inventory' の WHY）。'exchanges' は所持品を開け直しても外れたまま
  'exchanges',
  // ログインボーナス / プレゼント / シリアル / イベント / 無料ガチャ
  'loginBonuses',
  'gifts',
  'serials',
  'events',
  'gachas',
  // フレンド（とフレンドのオンライン状態 = requires friends）/ チャット / ギルド
  'friends',
  'presence',
  'chat',
  'guilds',
  // 通報と制裁を掛ける（kumo.reports）。本人の制裁の確認と異議（kumo.sanctions = sanctions.ts）はここに入れない = 常に出す
  // （founder 2026-09-27「プレイヤーの BAN は残す」→ 誤った BAN を取り消してもらう道が要る。下の SDK_NAMESPACES.moderation の WHY）
  'moderation',
  // AI の窓口（ブラウザ SDK には元から無い = 生成はプレイ中に走らせない方針）
  'ai',
  // お金の後回し（2026-09-29 creator/launch-money-b・MASTER-PLAN §0・docs/teams/creator/tasks/first-launch-money-api.md）:
  //   founder「お金に関しては後でいい。サーバーと DB がノリで使えるのが一番大事」。利用者への販売（kumo.store）は
  //   サーバーでも閉じた（Core core/money-close = どの鍵でも 403 feature_unavailable・運営の env KUMO_UNLOCKED_FEATURES だけで開く）。
  'payments',
  // 広告（kumo.ads）と所持品（kumo.inventory の get / consume）は 2026-10-19 creator/ads-open で出した（founder「広告を作り手に開ける準備
  //   （手順 1〜5）を始めて」・docs/teams/creator/tasks/ads-open.md・docs/teams/money/ads-earn-check-2026-10-19.md の手順 5）。
  //   サーバーは本番で KUMO_UNLOCKED_FEATURES=ads（10/17 founder「広告いれてよい」）= ads の口は開いていて、運び手の広告が開いたので
  //   所持品の口も開く（Core の hiddenFeatures.ts OPEN_PARTS_CARRIERS は some）。WHY 所持品も一緒か: リワード広告の報酬（ads.rewards の grants）の
  //   受け取り先が所持品 = 読めないと報酬が見えない。交換所は 'exchanges' が残る限り外れたまま（案 A の形）。
  //   以前（2026-09-29〜10-19）はここに 'ads' と 'inventory' があった。閉じ直すなら 2 つとも戻す（課金も閉じている間は運び手が無くなる）。
  //   作り手に配るのは、AppLixir の承認・鍵の入れ直し・テストの広告での通し（ads-earn-check の手順 1〜4）が済んでから（管理が founder の OK の後）
  // 利用者へのメール（2026-09-29 creator/mail-close・Kumo 管理の決定・docs/teams/creator/tasks/mail-close.md）:
  //   初回の公開では、クリエイターが利用者に送るメール（配信・予約・定期配信）を閉じる。サーバーも HIDDEN_FEATURES の 'mail' で閉じた
  //   （Core core/mail-close = 購読・状態の確認は 403 feature_unavailable・運営の env KUMO_UNLOCKED_FEATURES=mail だけで開く）。
  //   WHY 名前空間ごと外さないか: 止める（kumo.mail.unsubscribe）は閉じた後も必ず残す（サーバーも停止の口は開けたまま）。
  //   閉じる前に申し込んだ人が「もう要らない」と言える道を消さない = 同意の撤回は申し込みと同じくらい簡単に（GDPR Art 7(3)）。
  //   そこで外すのは申し込み（subscribe）と状態（status）のメソッドだけ（下の SDK_METHODS.mail）。
  //   kumo.auth の mailOptIn（登録・ログインのチェックボックス）も同じ 'mail' で型から外し、渡されても黙って無視する（mail.ts の applyMailOptIn の WHY）。
  //   「KUMODeck からのお知らせ」の欄（kumo.news = news.ts・config の kumoNews.optIn）は別の同意・別の機能なので残す（管理の決定 案 A）
  'mail'
] as const;

/**
 * 機能 → init() の返すオブジェクトでのその機能の名前空間（丸ごと外す単位）。機能の名前と SDK の名前がずれるもの（gachas → gacha 等）をここでそろえる。
 * 名前空間を持たない機能（exchanges = inventory の中のメソッド → 下の SDK_METHODS・ai = SDK に無い）は空。
 * kumo.social（profile / declareAge）は隠さない: 年齢の申告は広告・子どもの保護の前提で、サーバーでも常に ON（featureGate.ts の ALWAYS_ON）
 */
export const SDK_NAMESPACES = {
  // 課金（features.payments）の窓口は kumo.store（名前がずれる）。広告（features.ads）は kumo.ads
  payments: ['store'],
  ads: ['ads'],
  leaderboards: ['leaderboards'],
  achievements: ['achievements'],
  inventory: ['inventory'],
  exchanges: [],
  loginBonuses: ['loginBonus'],
  gifts: ['gifts'],
  serials: ['serials'],
  events: ['events'],
  gachas: ['gacha'],
  friends: ['friends'],
  presence: ['presence'],
  chat: ['chat'],
  guilds: ['guilds'],
  // WHY sanctions を外したか（2026-09-27 appeal-tools・Quality SEC-HIDE-03）: 以前は ['reports', 'sanctions'] で、通報を隠すと
  //   BAN された本人の異議（kumo.sanctions.mine / appeal）まで消え、サーバーでは開いている異議の口を使う道具が無かった。
  //   inventory を「読む・使う / 交換所」に分けたのと同じ考え方で、moderation の中を「通報・制裁を掛ける（隠す = reports）」と
  //   「BAN の異議（出す = sanctions）」に分けた。サーバーも異議の 3 本を機能の切り替えの外に置いている（featureGate.ts の ALWAYS_ON）ので、
  //   sanctions はどの機能の単位にも入れない（social と同じ扱い）。制裁を掛ける・取り消す関数は SDK に元から無い（運営の道具だけ）
  moderation: ['reports'],
  ai: [],
  // 利用者へのメール: kumo.mail は残す（unsubscribe を出し続ける）。外すのはメソッドだけ = 下の SDK_METHODS.mail
  mail: []
} as const satisfies Record<string, readonly string[]>;

/**
 * 機能 → 名前空間の中のメソッド（名前空間は残してメソッドだけ外す単位）。
 * WHY inventory を「読む・使う」と「交換」に分けるか（2026-09-27 リーダー経由の依頼）: 所持品を閉じると、課金の消費型の商品（コイン）と
 *   リワード広告の報酬をプレイヤーが読めなくなる（付与先が所持品のため）。founder は案 A（2026-09-27・MASTER-PLAN §1c）で
 *   「読む・使う（get / consume）だけ開ける」と決めたので、LAUNCH_HIDDEN_FEATURES から 'inventory' を外し、交換所（exchanges / exchange）は
 *   'exchanges' が一覧に残る限り外れたままにしている。付与（増やす関数）は SDK に元から無い（サーバーの課金・広告だけが増やす = gamedata.ts の信頼モデル）。
 *   他人の所持品を読む口（管理 API）も SDK には無い（sk_ の管理 API で、サーバーでも閉じたまま）
 */
export const SDK_METHODS = {
  exchanges: { inventory: ['exchanges', 'exchange'] },
  // 2026-09-29 creator/mail-close: 申し込み（subscribe）と状態（status）だけ外し、止める（unsubscribe）は残す（上の 'mail' の WHY）
  mail: { mail: ['subscribe', 'status'] }
} as const satisfies Record<string, Record<string, readonly string[]>>;

/** 隠す機能のキー（型）。mail.ts が「'mail' を隠している間は mailOptIn を型から外す」に使う */
export type HiddenFeature = (typeof LAUNCH_HIDDEN_FEATURES)[number];

/** 隠す名前空間（型）。index.ts の Kumo 型がこれを外す */
export type HiddenNamespace = (typeof SDK_NAMESPACES)[HiddenFeature][number];

/** 名前空間 NS の中で隠すメソッド名（型）。無ければ never */
type HiddenMethodsOf<NS extends PropertyKey> = {
  [F in HiddenFeature]: F extends keyof typeof SDK_METHODS
    ? NS extends keyof (typeof SDK_METHODS)[F]
      ? ((typeof SDK_METHODS)[F][NS] extends readonly (infer M)[] ? M : never)
      : never
    : never;
}[HiddenFeature];

/**
 * 実績を隠している間の kumo.stats の型: 結果の unlocked（達成した実績）と on('unlocked') を型から外す（2026-09-27 appeal-tools・Quality SEC-HIDE-04）。
 * WHY: 型の補完に実績が出ると「Kumo に実績の機能がある」と読める。閉じている間はサーバーが unlocked を常に [] で返すので、
 *   実行時の値はそのまま（中身の gamedata.ts は開けたときにそのまま動く）で、見える型だけを絞る。'achievements' を一覧から外せば元の型に戻る
 */
type StatsWithoutAchievements<S> = Omit<S, 'add' | 'submit' | 'flush' | 'on'> & {
  /** sum の stat に増分を足す（例: add('kills', 1)） */
  add(key: string, amount?: number): Promise<PublicStatsResult>;
  /** max / min / latest の stat に値を送る（例: submit('best_time', 61234)） */
  submit(key: string, value: number): Promise<PublicStatsResult>;
  /** まとめ待ちをすぐ送る */
  flush(): Promise<PublicStatsResult | null>;
  /** 送った結果（stats）を受け取る */
  on(event: 'stats', fn: (r: PublicStatsResult) => void): () => void;
};

/** 名前空間 K の公開の型（隠すメソッドを外し、実績を隠していれば stats を絞る） */
type PublicNamespace<T, K extends keyof T> = K extends 'stats'
  ? 'achievements' extends HiddenFeature
    ? StatsWithoutAchievements<T[K]>
    : T[K]
  : [HiddenMethodsOf<K>] extends [never]
    ? T[K]
    : Omit<T[K], HiddenMethodsOf<K> & keyof T[K]>;

/** init() の結果から、隠す名前空間とメソッドを外した型 */
export type WithoutHidden<T> = {
  [K in Exclude<keyof T, HiddenNamespace>]: PublicNamespace<T, K>;
};

/**
 * init() の結果から隠す名前空間を外し、残す名前空間の中の隠すメソッドを外す（名前空間は写しに差し替える = 元の gamedata の窓口は書き換えない）。
 * hidden はテスト用の差し替え（一覧から外したときに元どおりになるかを確かめる）。本番は常に LAUNCH_HIDDEN_FEATURES
 */
export function withoutHidden<T extends object>(kumo: T, hidden: readonly string[] = hiddenNow()): WithoutHidden<T> {
  const out = kumo as Record<string, unknown>;
  const namespaces = SDK_NAMESPACES as Record<string, readonly string[]>;
  const methods = SDK_METHODS as Record<string, Record<string, readonly string[]>>;
  for (const f of hidden) for (const ns of namespaces[f] ?? []) delete out[ns];
  for (const f of hidden) {
    for (const [ns, names] of Object.entries(methods[f] ?? {})) {
      const obj = out[ns];
      if (!obj || typeof obj !== 'object') continue;
      const copy = { ...(obj as Record<string, unknown>) };
      for (const m of names) delete copy[m];
      out[ns] = copy;
    }
  }
  return out as WithoutHidden<T>;
}

/**
 * テスト専用: init() が外す一覧を差し替える（null で元に戻す）。index.ts から export しない = 配布物の公開の API には無い。
 * WHY 要るか（2026-09-29 creator/launch-money-b）: 課金（kumo.store）を隠したので、ネイティブの課金の経路のテスト（native.test.ts）が
 *   init() の返り値から store を取れなくなった。中身（nativeStore.ts）は出し直しに備えて正しいまま保ちたいので、テストだけ一覧を空にして回す
 *   （CLI の KUMO_TEST_SHOW_LAUNCH_HIDDEN・MCP の hiddenFeatures と同じ考え方）。
 * WHY 入口を残してよいか: SDK は見せ方だけで、使えるかはサーバーが決める（閉じた口は 403 feature_unavailable）。ここを開けても守るべきものは開かない
 */
let testOverride: readonly string[] | null = null;
export function setLaunchHiddenForTests(list: readonly string[] | null): void {
  testOverride = list;
}
/** 今 init() が外す一覧（ふだんは LAUNCH_HIDDEN_FEATURES） */
export function hiddenNow(): readonly string[] {
  return testOverride ?? LAUNCH_HIDDEN_FEATURES;
}
/** この機能を init() の返り値から外しているか（index.ts のネイティブの課金の回収で使う） */
export function isHiddenNow(feature: string): boolean {
  return hiddenNow().includes(feature);
}
